import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { FaTrash } from 'react-icons/fa';
import { addComment, deleteComment, editComment } from '../../services/commentService';
import Avatar from '../common/Avatar';
import './CommentList.css';

/**
 * CommentList Component - Displays post comments with input field
 * 
 * @param {Object} props
 * @param {Array} props.comments - List of comments
 * @param {string} props.postId - ID of the post
 * @param {Object} props.currentUser - Current logged in user
 * @param {Function} props.onCommentAdded - Callback when comment is added
 * @param {Function} props.onCommentDeleted - Callback when comment is deleted
 * @param {boolean} props.expanded - Whether comments section is expanded
 * @param {Function} props.onViewAllComments - Callback when "View all comments" is clicked
 * @returns {JSX.Element}
 */
const CommentList = ({ 
  comments = [], 
  postId, 
  currentUser, 
  onCommentAdded, 
  onCommentDeleted, 
  expanded = false,
  onViewAllComments = () => {} 
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = React.useRef();
  
  // Handle comment input change
  const handleCommentChange = (e) => {
    setNewComment(e.target.value);
    setError(null);
  };
  
  // Handle submit comment
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !currentUser) {
      return;
    }
    
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const commentData = {
        post_id: postId,
        content: newComment.trim(),
        user_id: currentUser.id
      };
      
      const addedComment = await addComment(commentData);
      console.log('Service returned addedComment:', addedComment);
      
      // The comment should already have user data from the service
      if (addedComment) {
        setNewComment('');
        onCommentAdded(addedComment);
      } else {
        setError('Failed to add comment. Response was invalid.'); // Set specific error
      }

    } catch (err) {
      setError('Failed to add comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    if (!currentUser) return;
    
    try {
      await deleteComment(commentId);
      onCommentDeleted(commentId);
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment. Please try again.');
    }
  };

  // Helper to get profile link by user_id or username
  const getProfileLink = (user) => {
    if (!user) return '/profile';
    if (user.username) return `/profile/${user.username}`;
    if (user.user_id) return `/profile/${user.user_id}`; // fallback, ideally fetch username
    return '/profile';
  };

  // If not expanded and no comments, return null
  if (!expanded && (!comments || comments.length === 0)) {
    return null;
  }
  
  return (
    <div className={`comments-section ${expanded ? 'expanded' : ''}`}>
      {/* Comment Form - Only show when expanded or if there are comments */}
      {(expanded || comments.length > 0) && currentUser && (
        <form className="comment-form" onSubmit={handleSubmitComment}>
          <div className="comment-input-container">
            <Avatar 
              src={currentUser.profilePicture} 
              alt={currentUser.username}
              size="small"
            />
            <input
              type="text"
              className="comment-input"
              placeholder="Add a comment..."
              value={newComment}
              onChange={handleCommentChange}
              disabled={isSubmitting}
              aria-label="Add a comment"
              ref={inputRef}
            />
          </div>
          
          <div className="comment-form-actions">
            {error && <div className="comment-error">{error}</div>}
            <button 
              type="submit" 
              className="comment-submit-button"
              disabled={!newComment.trim() || isSubmitting}
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      )}
      
      {/* Comments List */}
      {comments.length > 0 && (
        <ul className="comments-list">
          {comments.map((comment) => {
            // Debug: Log comment structure
            console.log('Comment data:', comment);
            
            // Format comment date
            const formattedDate = formatDistanceToNow(
              new Date(comment.created_at),
              { addSuffix: true }
            );
            // Check if current user can delete this comment
            const canDelete = currentUser && currentUser.id === comment.user_id;
            return (
              <li key={comment.id} className="comment-item">
                <div className="comment-avatar">
                  <Avatar 
                    src={comment.user?.profilePicture}
                    alt={comment.user?.username || 'User'}
                    username={comment.user?.username}
                    size="small"
                  />
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <Link to={getProfileLink(comment.user)} className="comment-username">
                      {comment.user?.username || 'Unknown User'}
                    </Link>
                    <span className="comment-date">{formattedDate}</span>
                  </div>
                  <p className="comment-text">{comment.content}</p>
                </div>
                {canDelete && (
                  <button 
                    className="comment-delete-button"
                    onClick={() => handleDeleteComment(comment.id)}
                    aria-label="Delete comment"
                  >
                    <FaTrash />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      
      {/* Show message when expanded but no comments */}
      {expanded && comments.length === 0 && (
        <div className="no-comments-message">
          No comments yet. Be the first to comment!
        </div>
      )}
      
      {/* Show "See all comments" button when not expanded */}
      {!expanded && comments.length > 0 && (
        <button 
          className="view-all-comments-button"
          onClick={onViewAllComments}
          aria-label="View all comments"
        >
          View all {comments.length} comments
        </button>
      )}
    </div>
  );
};

export default CommentList; 